// First-open Terms & Conditions gate.
//
// Shown the FIRST time a customer opens their portal (when the server reports
// `customer.termsAcceptedAt == null`). Unlike the skippable set-password prompt,
// this is a BLOCKING gate: there is no skip / escape / backdrop-dismiss. The
// customer must read and tick the box, then Accept, before the portal renders.
//
// On Accept it posts to POST /api/customer/accept-terms (session-authed), which
// stamps the account so the gate never shows again. The returned Promise
// resolves only after a successful acceptance, so the caller can `await` it
// before mounting the portal sections.

import { api } from "@tkos/api-client";
import type { RetailerBranding } from "../types";

function esc(s: unknown): string {
  return String(s ?? "").replace(/[&<>"']/g, (c) =>
    c === "&" ? "&amp;" : c === "<" ? "&lt;" : c === ">" ? "&gt;" : c === '"' ? "&quot;" : "&#39;",
  );
}

/** The platform terms shown in the gate. Plain, customer-facing summary. */
function termsHtml(retailerName: string): string {
  const r = retailerName ? esc(retailerName) : "your kitchen retailer";
  return `
    <p>Welcome to your kitchen project portal. This portal is provided by The Kitchen OS Group Ltd
       ("the platform") on behalf of ${r}. Please read and accept these terms before continuing.</p>

    <h3>1. The platform &amp; your retailer</h3>
    <p>The platform provides the software you use to view your quotes, designs, documents,
       deliveries and messages. Your kitchen is supplied and installed by ${r}, who remains
       responsible for the products, pricing and work itself. The platform is not the seller
       of your kitchen.</p>

    <h3>2. Your account</h3>
    <p>Keep your sign-in link and any password you set private. The information shown here is
       specific to your project — please let ${r} know if any of your details are wrong.</p>

    <h3>3. Quotes &amp; payments</h3>
    <p>Quotes shown here are issued by ${r}. Any payments you choose to make through the portal
       are processed securely by Stripe; the platform never stores your card details. A quote is
       not a contract until you and ${r} agree it.</p>

    <h3>4. Your privacy</h3>
    <p>Your personal information is used only to deliver your kitchen project and is handled in
       line with UK data-protection law. It is shared with ${r} so they can complete your order,
       and is never sold.</p>

    <h3>5. Acceptable use</h3>
    <p>Please use this portal only for your own kitchen project and treat messages with ${r}
       courteously. Misuse may result in access being withdrawn.</p>

    <h3>6. Changes</h3>
    <p>These terms may be updated from time to time. If they change materially, we'll ask you to
       review and accept them again the next time you sign in.</p>

    <p class="cust-terms-note">By ticking the box below you confirm you have read and accept these
       Terms &amp; Conditions.</p>
  `;
}

/**
 * Show the blocking T&C gate. Resolves once the customer accepts (after the
 * acceptance is recorded server-side). Never rejects — on a network error it
 * shows an inline message and lets the customer retry.
 */
export function showTermsGate(retailer?: RetailerBranding | null): Promise<void> {
  return new Promise<void>((resolve) => {
    if (document.getElementById("cust-terms")) {
      // Already showing (shouldn't happen) — resolve to avoid a deadlock.
      resolve();
      return;
    }
    const retailerName = String((retailer && retailer.businessName) || "").trim();

    const overlay = document.createElement("div");
    overlay.id = "cust-terms";
    overlay.className = "cust-setpw-overlay cust-terms-overlay";
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");
    overlay.setAttribute("aria-label", "Terms and Conditions");

    const card = document.createElement("div");
    card.className = "cust-login-card cust-terms-card";

    const heading = document.createElement("h1");
    heading.className = "cust-login-heading";
    heading.textContent = "Terms & Conditions";

    const body = document.createElement("div");
    body.className = "cust-terms-body";
    body.tabIndex = 0; // keyboard-scrollable
    body.innerHTML = termsHtml(retailerName);

    const check = document.createElement("label");
    check.className = "cust-terms-check";
    const box = document.createElement("input");
    box.type = "checkbox";
    box.id = "cust-terms-agree";
    const boxText = document.createElement("span");
    boxText.textContent = "I have read and accept the Terms & Conditions";
    check.append(box, boxText);

    const accept = document.createElement("button");
    accept.type = "button";
    accept.className = "cust-login-primary cust-terms-accept";
    accept.textContent = "Accept & continue";
    accept.disabled = true;

    const err = document.createElement("div");
    err.className = "cust-login-err";
    err.setAttribute("role", "alert");
    err.setAttribute("aria-live", "polite");

    box.addEventListener("change", () => {
      accept.disabled = !box.checked;
    });

    // Block Escape — this gate is mandatory.
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") e.stopPropagation();
    };

    accept.addEventListener("click", () => {
      if (!box.checked) return;
      accept.disabled = true;
      accept.textContent = "Saving…";
      err.textContent = "";
      api<{ ok: boolean; error?: string }>("/customer/accept-terms", {
        method: "POST",
        body: JSON.stringify({ accepted: true }),
      })
        .then((d) => {
          if (!d.ok) {
            err.textContent = d.error || "Couldn't record your acceptance. Please try again.";
            accept.disabled = false;
            accept.textContent = "Accept & continue";
            return;
          }
          overlay.remove();
          document.removeEventListener("keydown", onKey, true);
          resolve();
        })
        .catch(() => {
          err.textContent = "Couldn't record your acceptance. Please try again.";
          accept.disabled = false;
          accept.textContent = "Accept & continue";
        });
    });

    card.append(heading, body, check, accept, err);
    overlay.appendChild(card);
    document.body.appendChild(overlay);
    document.addEventListener("keydown", onKey, true);
    setTimeout(() => body.focus(), 30);
  });
}
